import React from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { isValidEmailAddress } from '../../utils/emailValidation';
import RecipientContactAutocomplete from './RecipientContactAutocomplete';

const RecipientsSection = ({
  recipients,
  setRecipients,
  isOnlySigner,
  setIsOnlySigner,
  signingOrder,
  setSigningOrder,
  sequenceInputs,
  setSequenceInputs,
  handleSequenceChange,
  handleSequenceBlur,
  handleAddRecipient,
  handleRemoveRecipient,
  getRecipientColor,
  signingRoles,
  isTemplateMode = false,
  showRecipientFieldErrors = false,
  removingRecipientIndex = null,
  idPrefix = '',
  onPatchRecipient = null,
  savedContacts = [],
}) => {
  const updateRecipient = (index, patch) => {
    if (typeof onPatchRecipient === 'function') {
      onPatchRecipient(index, patch);
      return;
    }
    const updated = [...recipients];
    updated[index] = { ...updated[index], ...patch };
    setRecipients(updated);
  };

  return (
    <>
      <div className="recipient-options mb-3">
        <Form.Check
          type="checkbox"
          id={`${idPrefix}only-signer`}
          label={<span>I'm the only signer <i className="ri-information-line text-muted"></i></span>}
          checked={isOnlySigner}
          onChange={(e) => setIsOnlySigner(e.target.checked)}
        />
        <Form.Check
          type="checkbox"
          id={`${idPrefix}signing-order`}
          label={<span>Set signing order <a href="#view" className="text-decoration-none">View</a></span>}
          checked={signingOrder}
          disabled={recipients.length < 2}
          onChange={(e) => {
            const enabled = e.target.checked;
            setSigningOrder(enabled);
            if (enabled) {
              // Prefill sequence numbers immediately when signing order is enabled.
              setSequenceInputs(recipients.map((_, idx) => idx + 1));
            }
          }}
        />
      </div>

      {!isOnlySigner && (
        <>
          {recipients.map((recipient, index) => {
            const rawSeqInput = sequenceInputs[index];
            const rowStoredOrder = recipient.signature_order;
            const displaySigningOrder =
              rawSeqInput !== undefined
                ? rawSeqInput
                : rowStoredOrder != null && rowStoredOrder !== ''
                  ? rowStoredOrder
                  : index + 1;
            return (
            <div key={index} className="recipient-form">
              <div className="recipient-form-content">
                <div className="recipient-row d-flex align-items-start gap-3">
                  <div className="recipient-color-badge" style={{ backgroundColor: recipient.color || getRecipientColor(index) }}></div>
                  {signingOrder && (
                    <div className="recipient-form-indicator">
                      <div className="recipient-form-indicator-inner">
                        <div className="signing-order-number">
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={displaySigningOrder}
                            className="signing-order-input"
                            aria-label={`Signing order for recipient ${index + 1}`}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val !== '' && !/^\d+$/.test(val)) return;
                              setSequenceInputs((prev) => {
                                const next = [...prev];
                                next[index] = val;
                                return next;
                              });
                            }}
                            onBlur={(e) => {
                              const blurHandler = handleSequenceBlur || handleSequenceChange;
                              blurHandler(index, e.target.value);
                            }}
                          />
                        </div>
                        <div className="recipient-rail" />
                      </div>
                    </div>
                  )}

                  {!(isTemplateMode && recipient.isVariable) && (
                    <div className="recipient-field flex-grow-1">
                      <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                      <RecipientContactAutocomplete
                        field="name"
                        contacts={savedContacts}
                        placeholder="Enter name"
                        value={recipients[index].name}
                        showIcon
                        iconClassName="ri-user-line"
                        onChange={(name) => updateRecipient(index, { name })}
                        onSelectContact={(contact) => {
                          updateRecipient(index, {
                            name: contact.name || recipients[index].name,
                            email: contact.email || recipients[index].email,
                          });
                        }}
                      />
                    </div>
                  )}

                  {!(isTemplateMode && recipient.isVariable) && (
                    <div className="recipient-field flex-grow-1">
                      <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                      {(() => {
                        const emailTrim = String(recipients[index].email || '').trim();
                        const invalidFormat = emailTrim !== '' && !isValidEmailAddress(emailTrim);
                        const requiredEmpty = showRecipientFieldErrors && emailTrim === '';
                        const emailInvalid = invalidFormat || requiredEmpty;
                        const feedback = invalidFormat
                          ? 'Enter a valid email address (e.g. name@example.com).'
                          : (requiredEmpty ? 'Email is required.' : '');
                        return (
                          <RecipientContactAutocomplete
                            field="email"
                            contacts={savedContacts}
                            placeholder="Enter email"
                            value={recipients[index].email}
                            isInvalid={emailInvalid}
                            feedback={feedback}
                            onChange={(email) => updateRecipient(index, { email })}
                            onSelectContact={(contact) => {
                              updateRecipient(index, {
                                name: contact.name || recipients[index].name,
                                email: contact.email || recipients[index].email,
                              });
                            }}
                          />
                        );
                      })()}
                    </div>
                  )}

                  <div className="recipient-field recipient-role-field">
                    <Form.Label>Role</Form.Label>
                    <Form.Select
                      value={recipient.role}
                      onChange={(e) => {
                        const role = e.target.value;
                        const patch = { role };
                        if (role !== 'Needs to Sign') {
                          patch.isVariable = false;
                          patch.placeholder = '';
                        }
                        updateRecipient(index, patch);
                      }}
                      className="recipient-role-select"
                    >
                      {signingRoles.map((role, roleIndex) => (
                        <option key={roleIndex} value={role.label}>{role.label}</option>
                      ))}
                    </Form.Select>
                  </div>

                  {isTemplateMode && recipient.role === 'Needs to Sign' && (
                    <div className="recipient-field flex-grow-1">
                      <Form.Label>Template Mode</Form.Label>
                      <Form.Check
                        type="switch"
                        id={`${idPrefix}variable-signee-${index}`}
                        label="Variable signee"
                        checked={!!recipient.isVariable}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          if (!enabled) {
                            updateRecipient(index, { isVariable: false, placeholder: '' });
                            return;
                          }
                          const placeholder = recipient.placeholder || `signee_${index + 1}`;
                          updateRecipient(index, {
                            isVariable: true,
                            placeholder,
                            name: placeholder,
                            email: '',
                          });
                        }}
                      />
                      {!!recipient.isVariable && (
                        <Form.Control
                          className="mt-2"
                          type="text"
                          placeholder="e.g. signee_1"
                          value={recipient.placeholder || ''}
                          onChange={(e) => {
                            const placeholder = e.target.value;
                            updateRecipient(index, {
                              placeholder,
                              name: placeholder || recipient.name,
                              email: '',
                            });
                          }}
                        />
                      )}
                    </div>
                  )}

                  {recipients.length > 1 && (
                    <div className="recipient-actions d-flex align-items-end">
                      <Button
                        variant="link"
                        type="button"
                        className="text-danger px-2 d-inline-flex align-items-center justify-content-center"
                        style={{ minWidth: '2rem', minHeight: '2rem' }}
                        disabled={removingRecipientIndex !== null}
                        aria-busy={removingRecipientIndex === index}
                        onClick={() => handleRemoveRecipient(index)}
                      >
                        {removingRecipientIndex === index ? (
                          <Spinner animation="border" size="sm" className="text-danger" role="status">
                            <span className="visually-hidden">Deleting…</span>
                          </Spinner>
                        ) : (
                          <i className="ri-delete-bin-line" aria-hidden />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
          })}

          <div className="mt-3">
            <Button variant="outline-primary" size="sm" onClick={handleAddRecipient}>
              <i className="ri-add-line me-2"></i>
              Add Recipient
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default RecipientsSection;
